import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import MaterialTable from "@material-table/core";
import { ExportCsv, ExportPdf } from "@material-table/exporters";
import toast, { Toaster } from "react-hot-toast";
import { Check, X, Trash2 } from "lucide-react";
import "./document.css";
import "./loader.css";

const DocumentVerification = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem("isLoggedIn");
    if (isAuthenticated !== "true") {
      navigate("/");
    }
  }, [navigate]);

  const fetchData = () => {
    setLoading(true);
    fetch("https://api.sgtu.ac.in/api/documentVerification")
      .then((response) => response.json())
      .then((data) => {
        setTableData(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching document verification data:", error);
        toast.error("Error fetching data");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleVerifyReject = async (id, status) => {
    try {
      const res = await fetch(
        `https://api.sgtu.ac.in/api/documentVerification/verify/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isVerified: status }),
        }
      );

      const data = await res.json();

      if (res.ok) {
        toast.success(status ? "Verified!" : "Rejected!");

        //  Table reload na ho — bas ek row update ho:
        setTableData((prev) =>
          prev.map((item) =>
            item._id === id ? { ...item, isVerified: status } : item
          )
        );
      } else {
        toast.error(data.message || "Failed to update status");
      }
    } catch (error) {
      toast.error("Error updating verification status");
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Do you want to delete this entry?");
    if (!confirmDelete) return;

    try {
      const res = await fetch(
        `https://api.sgtu.ac.in/api/documentVerification/${id}`,
        {
          method: "DELETE",
        }
      );
      if (!res.ok) throw new Error("Failed to delete entry");
      toast.success("Entry deleted successfully");

      // Remove deleted row from local state
      setTableData((prev) => prev.filter((row) => row._id !== id));
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete entry");
    }
  };

  const commonCellStyle = {
    fontSize: "12px",
    whiteSpace: "nowrap",
  };

  const actionButtonStyle = {
    backgroundColor: "#f2f2f2",
    padding: "6px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    width: "32px",
    height: "32px",
  };

  const columns = [
    {
      title: "Category",
      field: "category",
      headerStyle: commonCellStyle,
      cellStyle: commonCellStyle,
    },
    {
      title: "Enrollment",
      field: "enrollment",
      headerStyle: commonCellStyle,
      cellStyle: commonCellStyle,
    },
    // { title: "Session", field: "session", headerStyle: commonCellStyle, cellStyle: commonCellStyle },
    {
      title: "Programme",
      field: "programme",
      headerStyle: commonCellStyle,
      cellStyle: commonCellStyle,
    },
    {
      title: "Candidate Name",
      field: "cname",
      headerStyle: commonCellStyle,
      cellStyle: commonCellStyle,
    },
    // { title: "Father Name", field: "fname", headerStyle: commonCellStyle, cellStyle: commonCellStyle },
    // { title: "Mother Name", field: "mname", headerStyle: commonCellStyle, cellStyle: commonCellStyle },
    // { title: "Email", field: "email", headerStyle: commonCellStyle, cellStyle: commonCellStyle },
    {
      title: "City",
      field: "city",
      headerStyle: commonCellStyle,
      cellStyle: commonCellStyle,
    },
    // { title: "Pincode", field: "pincode", headerStyle: commonCellStyle, cellStyle: commonCellStyle },
    {
      title: "Fees",
      field: "fees",
      headerStyle: commonCellStyle,
      cellStyle: commonCellStyle,
    },
    {
      title: "Marksheet",
      field: "marksheet",
      render: (rowData) => (
        <a
          href={`https://api.sgtu.ac.in/api/documentVerification/view/${rowData._id}/marksheet`}
          target="_blank"
          rel="noopener noreferrer"
        >
          View
        </a>
      ),
      headerStyle: commonCellStyle,
      cellStyle: commonCellStyle,
    },
    {
      title: "PAN Card",
      field: "panCard",
      render: (rowData) => (
        <a
          href={`https://api.sgtu.ac.in/api/documentVerification/view/${rowData._id}/panCard`}
          target="_blank"
          rel="noopener noreferrer"
        >
          View
        </a>
      ),
      headerStyle: commonCellStyle,
      cellStyle: commonCellStyle,
    },
    {
      title: "Aadhar Card",
      field: "aadharCard",
      render: (rowData) => (
        <a
          href={`https://api.sgtu.ac.in/api/documentVerification/view/${rowData._id}/aadharCard`}
          target="_blank"
          rel="noopener noreferrer"
        >
          View
        </a>
      ),
      headerStyle: commonCellStyle,
      cellStyle: commonCellStyle,
    },
    {
      title: "Document",
      field: "document",
      render: (rowData) => (
        <a
          href={`https://api.sgtu.ac.in/api/documentVerification/view/${rowData._id}/document`}
          target="_blank"
          rel="noopener noreferrer"
        >
          View
        </a>
      ),
      headerStyle: commonCellStyle,
      cellStyle: commonCellStyle,
    },
    {
      title: "Photo",
      field: "photo",
      render: (rowData) => (
        <a
          href={`https://api.sgtu.ac.in/api/documentVerification/view/${rowData._id}/photo`}
          target="_blank"
          rel="noopener noreferrer"
        >
          View
        </a>
      ),
      headerStyle: commonCellStyle,
      cellStyle: commonCellStyle,
    },
    // {
    //   title: "Created At",
    //   field: "createdAt",
    //   render: (rowData) => {
    //     const date = new Date(rowData.createdAt);
    //     const formattedDate = `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
    //     return formattedDate;
    //   },
    //   headerStyle: commonCellStyle,
    //   cellStyle: commonCellStyle,
    // },
    {
      title: "Action",
      field: "actions",
      render: (rowData) => (
        <div className="action-buttons">
          <button
            className="approve-btn"
            onClick={() => handleVerifyReject(rowData._id, true)}
            disabled={rowData.isVerified === true}
            title="Verify"
          >
            <Check size={14} />
          </button>

          <button
            className="reject-btn"
            onClick={() => handleVerifyReject(rowData._id, false)}
            disabled={rowData.isVerified === false}
            title="Reject"
          >
            <X size={14} />
          </button>

          <button
            className="delete-btn"
            onClick={() => handleDelete(rowData._id)}
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ),
      headerStyle: { fontSize: "12px", textAlign: "center" },
      cellStyle: { fontSize: "12px", textAlign: "center" },
    },
  ];

  return (
    <div className="dashboard-wrapper">
      <Toaster position="top-center" />
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
      />
      <main className="dashboard-main">
        <Header />
        <div className="main-section">
          {loading ? (
            <div className="loader-main">
              <div className="loader-box">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ) : (
            <div className="table-scroll-container">
              <MaterialTable
                title="Document Verifications Data"
                columns={columns}
                data={tableData}
                options={{
                  tableLayout: "fixed",
                  search: true,
                  paging: true,
                  pageSize: 10,
                  pageSizeOptions: [5, 10, 20],
                  padding: "dense",
                  exportMenu: [
                    {
                      label: "Export to Excel",
                      exportFunc: (cols, datas) =>
                        ExportCsv(cols, datas, "DocumentVerification"),
                    },
                    {
                      label: "Export to PDF",
                      exportFunc: (cols, datas) =>
                        ExportPdf(cols, datas, "DocumentVerification"),
                    },
                  ],
                }}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DocumentVerification;
